<script lang="ts">
  import { getContext, onMount, createEventDispatcher } from "svelte";
  import { debounce } from "app/utils";
  import WrapField from "$components/form/Fields/WrapField.svelte";
  import cx from "classnames";
  import type { createFormStore } from "$components/form/store";

  export let name: string;
  export let label = "";
  export let labelHidden = false;
  export let variant: InputVariants = "primary";
  export let textSize: `text-${string}` = "text-sm sm:text-[13px]";
  export let fontWeight: `font-${string}` = "font-normal";
  export let inputProps: Record<string, any> = {};
  export let charactersLimit: number | undefined = 0;
  export let wrapperClassname = "";
  export let validationRules: Record<string, any> = {};
  export let help = "";
  export let autoFocus = false;
  export let value: string = "";

  const formStore: ReturnType<typeof createFormStore> | undefined = getContext("form");
  let ref: HTMLInputElement;

  const dispatch = createEventDispatcher();
  const debouncedDispatch = debounce((value: string) => {
    dispatch("change", value);
  }, 300);

  type InputVariants = "primary" | "secondary";
  const variants = {
    // MAIN VARIANTS
    primary: cx(
      "rounded-[10px] px-3 bg-transparent border-[0.5px] border-black/20", // light mode styles
      "dark:bg-transparent dark:text-neutral-100 dark:border-white/10" // dark mode styles
    ),
    secondary: cx(
      "bg-transparent dark:bg-transparent", // light mode styles
      "dark:focus:text-neutral-300 dark:text-neutral-400" // dark mode styles
    ),
  };

  // Extend validation rules if a character limit is set
  $: if (charactersLimit) {
    validationRules.maxLength = {
      value: charactersLimit,
      message: `You have exceeded the maximum number of characters (${charactersLimit})`,
    };
  }

  // Compute the help text based on the characters limit
  $: help = charactersLimit ? `${value?.length || 0}/${charactersLimit}` : help;

  function onInput(event: Event) {
    const target = event.target as HTMLInputElement;
    value = target.value;

    if (formStore) {
      formStore.setField(name, value);
    } else {
      debouncedDispatch(value);
    }
  }

  onMount(() => {
    if (formStore) {
      formStore.addRef(name, ref);
      value = $formStore?.values[name];
    }
    if (autoFocus) {
      ref.focus();
    }
  });
</script>

<WrapField {label} {name} {wrapperClassname} {validationRules} {help} {labelHidden}>
  <input
    id={name}
    {name}
    bind:value
    bind:this={ref}
    on:input={onInput}
    class="relative py-2.5 font-normal text-neutral-600 transition-colors
      placeholder:text-neutral-400 focus:border-black/40 focus:outline-none dark:text-neutral-300
      dark:placeholder:text-neutral-600 focus:dark:border-white/30
      {textSize} {fontWeight} {variants[variant]}
    ]}"
    {...$$restProps}
    {...inputProps}
  />
</WrapField>
